/* ========================================================================
   new-arrivals 専用の facet 照合
   ======================================================================== */
(() => {
  const AuremePlpUtils = window.AuremePlpUtils;
  if (!AuremePlpUtils) return;

  const STOCK_LABEL_IN_STOCK = '在庫あり';
  const STOCK_LABEL_OUT_OF_STOCK = '在庫切れ';
  const STOCK_LABEL_ALL = 'すべて';
  const SALES_KIND_REGULAR = '通常商品';
  const SALES_KIND_PREORDER = '予約商品';
  const GROUP_TYPES_WITH_DIRECT_LABEL = ['swatch', 'size', 'pill'];
  const GROUP_TYPES_WITH_CATEGORY_TOKENS = ['category', 'checkbox'];

  const { getFilterForm, parsePositiveInteger, syncFilterOptionState } = AuremePlpUtils;

  function getNewArrivalVirtualFacetInputs(root) {
    const filterForm = getFilterForm(root);
    if (!filterForm) return [];

    return Array.from(
      filterForm.querySelectorAll('[data-aureme-filter-option] input[name]'),
    ).filter((input) => input.type === 'checkbox' || input.type === 'radio');
  }

  function getNewArrivalPriceCriteria(root) {
    const filterForm = getFilterForm(root);
    if (!filterForm) return null;

    const priceInputs = Array.from(
      filterForm.querySelectorAll('[data-filter-type="price"] input[type="number"][name]'),
    );
    if (!priceInputs.length) return null;

    const minInput = priceInputs[0];
    const maxInput = priceInputs[1];
    const minValue = parsePositiveInteger(minInput?.value, 0);
    const maxValue = parsePositiveInteger(maxInput?.value, 0);

    if (!minValue && !maxValue) return null;

    return {
      key: 'aureme-new-arrival-price',
      type: 'price',
      operator: 'AND',
      paramName: '',
      selectionType: 'number',
      inputs: [],
      activeCriteria: [
        {
          min: minValue || null,
          max: maxValue || null,
        },
      ],
    };
  }

  function buildNewArrivalCategoryTokens(input) {
    const tokens = new Set();
    const rawValue = (input.value || '').trim();
    const filterLabel = (input.dataset.filterLabel || '').trim();

    if (rawValue) {
      tokens.add(rawValue);
      /* '__' 区切りを除いた形も候補に加え、タグ側の表記揺れ（区切りの有無）どちらとも照合できるようにする */
      tokens.add(rawValue.replaceAll('__', ''));
    }

    if (filterLabel) {
      tokens.add(filterLabel);
    }

    return Array.from(tokens).filter(Boolean);
  }

  function isNewArrivalStockGroup(groupOrType) {
    if (typeof groupOrType === 'string') {
      return groupOrType === 'stock';
    }

    return groupOrType?.type === 'stock' || groupOrType?.paramName === 'filter.v.availability';
  }

  function getNewArrivalFacetCriterion(input, groupOrType) {
    const filterLabel = (input.dataset.filterLabel || input.value || '').trim();
    const groupType = typeof groupOrType === 'string' ? groupOrType : groupOrType?.type || '';

    if (GROUP_TYPES_WITH_DIRECT_LABEL.includes(groupType)) {
      return filterLabel;
    }

    if (isNewArrivalStockGroup(groupOrType)) {
      return filterLabel;
    }

    if (GROUP_TYPES_WITH_CATEGORY_TOKENS.includes(groupType)) {
      return buildNewArrivalCategoryTokens(input);
    }

    return filterLabel;
  }

  function normalizeNewArrivalSalesKindValue(value) {
    const normalizedValue = String(value || '').trim();
    if (!normalizedValue) return '';

    if (normalizedValue.includes('予約')) {
      return SALES_KIND_PREORDER;
    }

    if (normalizedValue.includes('通常')) {
      return SALES_KIND_REGULAR;
    }

    return normalizedValue;
  }

  function getNewArrivalFacetGroups(root) {
    const facetInputs = getNewArrivalVirtualFacetInputs(root);
    const groups = new Map();

    for (const input of facetInputs) {
      const filterGroup = input.closest('[data-filter-group]');
      const groupKey = filterGroup?.id || input.getAttribute('name') || '';
      if (!groupKey) continue;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          key: groupKey,
          type: filterGroup?.dataset.filterType || '',
          operator: filterGroup?.dataset.filterOperator || 'OR',
          paramName: input.getAttribute('name') || '',
          selectionType: input.type || 'checkbox',
          inputs: [],
          activeCriteria: [],
        });
      }

      const group = groups.get(groupKey);
      group.inputs.push(input);

      if (input.checked) {
        group.activeCriteria.push(getNewArrivalFacetCriterion(input, group));
      }
    }

    const groupList = Array.from(groups.values());
    const priceCriteria = getNewArrivalPriceCriteria(root);
    if (priceCriteria) {
      groupList.push(priceCriteria);
    }

    return groupList;
  }

  function isVariantLevelNewArrivalGroup(group) {
    return (
      group.type === 'swatch' ||
      group.type === 'size' ||
      group.type === 'pill' ||
      group.type === 'price'
    );
  }

  function matchesNewArrivalCriteriaByOperator(group, criteria, criterionMatcher) {
    return group.operator === 'AND'
      ? criteria.every(criterionMatcher)
      : criteria.some(criterionMatcher);
  }

  /*
    無効化判定では「この選択肢を選んだ場合」の条件を仮合成する。
    radio は置換、checkbox は追加にし、実際の UI 選択契約と揃える。
  */
  function getNewArrivalGroupCriteria(group, candidateGroupKey, candidateCriterion) {
    if (group.key !== candidateGroupKey) {
      return [...group.activeCriteria];
    }

    return group.selectionType === 'radio'
      ? [candidateCriterion]
      : [...group.activeCriteria, candidateCriterion];
  }

  /*
   * 候補 option を選んだ場合の条件を一度組み立て、
   * 商品全体で判定する group と variant 配列で判定する group に分ける。
   */
  function partitionNewArrivalFacetGroups(groups, candidateGroupKey, candidateCriterion) {
    const productLevelGroups = [];
    const variantLevelGroups = [];

    for (const group of groups) {
      const criteria = getNewArrivalGroupCriteria(group, candidateGroupKey, candidateCriterion);
      if (!criteria.length) continue;

      const groupEntry = { group, criteria };
      if (isVariantLevelNewArrivalGroup(group)) {
        variantLevelGroups.push(groupEntry);
      } else {
        productLevelGroups.push(groupEntry);
      }
    }

    if (!variantLevelGroups.length) {
      return { productLevelGroups, variantLevelGroups };
    }

    for (let index = productLevelGroups.length - 1; index >= 0; index -= 1) {
      const groupEntry = productLevelGroups[index];
      if (!isNewArrivalStockGroup(groupEntry.group)) continue;
      if (isDefaultNewArrivalStockCriteria(groupEntry.criteria)) continue;

      /* 色やサイズと併用する在庫条件は、商品全体ではなく同一バリエーションの在庫で判定する */
      variantLevelGroups.push(groupEntry);
      productLevelGroups.splice(index, 1);
    }

    return { productLevelGroups, variantLevelGroups };
  }

  /*
   * variant facet の表現差をここで吸収する。
   * UI の価格は円、facet JSON は cents なので価格条件だけ単位変換して照合する。
   */
  function variantMatchesNewArrivalGroup(variantFacet, group, criteria) {
    if (!criteria.length) return true;

    let variantValue = '';
    let comparedCriteria = criteria;

    if (group.type === 'swatch') {
      variantValue = variantFacet.color || '';
    } else if (group.type === 'size') {
      variantValue = variantFacet.size || '';
    } else if (group.type === 'pill') {
      variantValue = normalizeNewArrivalSalesKindValue(variantFacet.salesKind);
      comparedCriteria = criteria.map(normalizeNewArrivalSalesKindValue).filter(Boolean);
    } else if (isNewArrivalStockGroup(group)) {
      variantValue = variantFacet.available ? STOCK_LABEL_IN_STOCK : STOCK_LABEL_OUT_OF_STOCK;
    } else if (group.type === 'price') {
      const priceCriteria = criteria[0] || {};

      if (priceCriteria.min && variantFacet.price < priceCriteria.min * 100) {
        return false;
      }
      if (priceCriteria.max && variantFacet.price > priceCriteria.max * 100) {
        return false;
      }

      return true;
    } else {
      return true;
    }

    return matchesNewArrivalCriteriaByOperator(
      group,
      comparedCriteria,
      (criterion) => criterion === variantValue,
    );
  }

  function isDefaultNewArrivalStockCriteria(criteria) {
    return criteria.some((criterion) => criterion.includes(STOCK_LABEL_ALL));
  }

  function variantMatchesNewArrivalCrossGroup(variantFacet, group, criteria) {
    if (!criteria.length) return true;

    /* 他グループ照合時の「すべて一致」条件は、同一バリエーションに全値を要求せず 1 値一致で十分とする */
    if (group.operator === 'AND') {
      return criteria.some((criterion) =>
        variantMatchesNewArrivalGroup(variantFacet, group, [criterion]),
      );
    }

    return variantMatchesNewArrivalGroup(variantFacet, group, criteria);
  }

  function variantPassesOtherGroups(variantFacet, variantLevelGroups, currentGroupEntry) {
    for (const otherGroupEntry of variantLevelGroups) {
      if (otherGroupEntry === currentGroupEntry) continue;
      if (
        !variantMatchesNewArrivalCrossGroup(
          variantFacet,
          otherGroupEntry.group,
          otherGroupEntry.criteria,
        )
      ) {
        return false;
      }
    }

    return true;
  }

  /*
   * variant 条件は、候補 group と他 group が同じ variant 上で両立するかまで確認する。
   * 商品単位で OR すると、色とサイズが別 variant に分かれた組み合わせまで有効扱いになる。
   */
  function hasNewArrivalVariantMatch(facetItem, groupEntry, variantLevelGroups, criteria) {
    return facetItem.variants.some(
      (variantFacet) =>
        variantMatchesNewArrivalGroup(variantFacet, groupEntry.group, criteria) &&
        variantPassesOtherGroups(variantFacet, variantLevelGroups, groupEntry),
    );
  }

  function productMatchesNewArrivalVariantGroups(facetItem, variantLevelGroups) {
    if (!variantLevelGroups.length) return true;

    for (const groupEntry of variantLevelGroups) {
      const { group, criteria } = groupEntry;
      if (!criteria.length) continue;

      if (group.operator === 'AND') {
        /* 同一グループの「すべて一致」値は別バリエーションで満たせるようにしつつ、他グループ条件との整合だけは保つ */
        for (const criterion of criteria) {
          if (!hasNewArrivalVariantMatch(facetItem, groupEntry, variantLevelGroups, [criterion])) {
            return false;
          }
        }

        continue;
      }

      if (!hasNewArrivalVariantMatch(facetItem, groupEntry, variantLevelGroups, criteria)) {
        return false;
      }
    }

    return true;
  }

  function productMatchesNewArrivalGroup(facetItem, group, criteria) {
    if (!criteria.length) return true;

    if (isNewArrivalStockGroup(group)) {
      if (isDefaultNewArrivalStockCriteria(criteria)) {
        return true;
      }

      const stockMatcher = (criterion) => {
        if (criterion.includes(STOCK_LABEL_IN_STOCK)) return facetItem.available;
        if (criterion.includes(STOCK_LABEL_OUT_OF_STOCK)) return !facetItem.available;
        return true;
      };

      return matchesNewArrivalCriteriaByOperator(group, criteria, stockMatcher);
    }

    if (group.type === 'category' || group.type === 'checkbox') {
      const categoryMatcher = (criterionTokens) =>
        criterionTokens.some((criterionToken) => facetItem.tags.includes(criterionToken));

      return matchesNewArrivalCriteriaByOperator(group, criteria, categoryMatcher);
    }

    return true;
  }

  function facetItemMatchesNewArrivalCriteria(
    facetItem,
    groups,
    candidateGroupKey,
    candidateCriterion,
  ) {
    const { productLevelGroups, variantLevelGroups } = partitionNewArrivalFacetGroups(
      groups,
      candidateGroupKey,
      candidateCriterion,
    );

    for (const { group, criteria } of productLevelGroups) {
      if (!productMatchesNewArrivalGroup(facetItem, group, criteria)) {
        return false;
      }
    }

    return productMatchesNewArrivalVariantGroups(facetItem, variantLevelGroups);
  }

  function syncNewArrivalVirtualFacetAvailability(
    _root,
    groups,
    sourceFacetItems,
    isCurrentRender,
  ) {
    if (!groups.length) return;
    if (typeof isCurrentRender !== 'function' || !isCurrentRender()) return;

    /* 各選択肢が有効か無効かは元データ全体を使ってクライアント側で判定し、グループごとの再取得を避ける */
    for (const group of groups) {
      for (const input of group.inputs) {
        if (input.checked) {
          input.disabled = false;
        } else {
          const candidateCriterion = getNewArrivalFacetCriterion(input, group);
          const isAvailable = sourceFacetItems.some((facetItem) =>
            facetItemMatchesNewArrivalCriteria(facetItem, groups, group.key, candidateCriterion),
          );
          input.disabled = !isAvailable;
        }

        const option = input.closest('[data-aureme-filter-option]');
        if (!option) continue;
        syncFilterOptionState(option);
      }
    }
  }

  window.AuremePlpNewArrival = Object.assign(window.AuremePlpNewArrival || {}, {
    facetItemMatchesNewArrivalCriteria,
    getNewArrivalFacetGroups,
    syncNewArrivalVirtualFacetAvailability,
  });
})();
